import React, { useEffect, useMemo, useState } from "react";
import { FaBell, FaCheck, FaCheckCircle, FaTimes } from "react-icons/fa";
import { FiCalendar, FiCheckCircle, FiEdit, FiEye, FiPlus } from "react-icons/fi";
import { ResponsiveContainer, BarChart, Bar as RechartsBar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend as RechartsLegend, LineChart, Line as RechartsLine } from "recharts";
import { auth, db } from "../../firebase";
import { appointmentService } from "../../services/appointmentService";
import { doc, getDoc } from "firebase/firestore";
import { useLanguage } from "../../contexts/LanguageContext";
import "./staffcss/medicaldashboard.css";
import "./staffcss/contentdashboard.css";

const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split("T")[0];
};

const computeAnalytics = (allAppointments, scheduledAppointments) => {
  const today = getTodayDate();
  const statusCounts = {
    PENDING: 0,
    CONFIRMED: 0,
    ACCEPTED: 0,
    COMPLETED: 0,
    CANCELLED_BY_USER: 0,
    CANCELLED_BY_PRO: 0
  };
  const todayStatusCounts = {
    PENDING: 0,
    CONFIRMED: 0,
    ACCEPTED: 0,
    COMPLETED: 0,
    CANCELLED_BY_USER: 0,
    CANCELLED_BY_PRO: 0
  };
  const bookingTypeCounts = { ONLINE: 0, WALKIN: 0, WALK_IN: 0 };
  const serviceCounts = {};
  const weeklyTrends = {};

  allAppointments.forEach((appointment) => {
    if (statusCounts[appointment.status] !== undefined) {
      statusCounts[appointment.status] += 1;
    }

    if (appointment.date === today && todayStatusCounts[appointment.status] !== undefined) {
      todayStatusCounts[appointment.status] += 1;
    }

    if (bookingTypeCounts[appointment.bookingType] !== undefined) {
      bookingTypeCounts[appointment.bookingType] += 1;
    }

    if (appointment.serviceName) {
      if (!serviceCounts[appointment.serviceName]) {
        serviceCounts[appointment.serviceName] = {
          PENDING: 0,
          CONFIRMED: 0,
          ACCEPTED: 0,
          COMPLETED: 0,
          CANCELLED: 0
        };
      }

      if (appointment.status === "CANCELLED_BY_USER" || appointment.status === "CANCELLED_BY_PRO") {
        serviceCounts[appointment.serviceName].CANCELLED += 1;
      } else if (serviceCounts[appointment.serviceName][appointment.status] !== undefined) {
        serviceCounts[appointment.serviceName][appointment.status] += 1;
      }
    }

    if (appointment.date) {
      const appointmentDate = new Date(`${appointment.date}T00:00:00`);
      if (!Number.isNaN(appointmentDate.getTime())) {
        const dateKey = appointmentDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
        if (!weeklyTrends[dateKey]) {
          weeklyTrends[dateKey] = { COMPLETED: 0, CANCELLED: 0 };
        }

        if (appointment.status === "COMPLETED") {
          weeklyTrends[dateKey].COMPLETED += 1;
        } else if (appointment.status === "CANCELLED_BY_USER" || appointment.status === "CANCELLED_BY_PRO") {
          weeklyTrends[dateKey].CANCELLED += 1;
        }
      }
    }
  });

  return {
    statusCounts,
    todayStatusCounts,
    bookingTypeCounts,
    serviceCounts,
    weeklyTrends,
    total: allAppointments.length,
    todayAppointments: scheduledAppointments.filter((appointment) => appointment.date === today),
    pendingAppointments: allAppointments.filter((appointment) => appointment.status === "PENDING")
  };
};

const MedicalDashboard = ({ setShowWalkinModal, staffProfile, canReviewAppointments = false, proNotifications = [], onGoToReview, onGoToNotifications }) => {
  const { getTranslations } = useLanguage();
  const t = getTranslations(false, false, false, true);
  const [proAppointments, setProAppointments] = useState([]);
  const [pendingReviewAppointments, setPendingReviewAppointments] = useState([]);
  const [confirmedAppointments, setConfirmedAppointments] = useState([]);
  const [loadingProAppointments, setLoadingProAppointments] = useState(true);
  const [loadingPendingReview, setLoadingPendingReview] = useState(true);
  const [loadingConfirmedAppointments, setLoadingConfirmedAppointments] = useState(true);
  const [loadingCharts, setLoadingCharts] = useState(true);
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentUserFirstName, setCurrentUserFirstName] = useState("");

  useEffect(() => {
    let unsubscribePro = () => {};
    let unsubscribePending = () => {};
    let unsubscribeConfirmed = () => {};

    const user = auth.currentUser;
    if (!user) {
      setLoadingProAppointments(false);
      setLoadingPendingReview(false);
      setLoadingConfirmedAppointments(false);
      setLoadingCharts(false);
      return () => {};
    }

    setLoadingProAppointments(true);
    setLoadingPendingReview(true);
    setLoadingConfirmedAppointments(true);
    setLoadingCharts(true);

    unsubscribePro = appointmentService.subscribeToProAppointments(user.uid, (appointments) => {
      setProAppointments(appointments);
      setLoadingProAppointments(false);
    });

    if (canReviewAppointments && staffProfile) {
      unsubscribePending = appointmentService.subscribeToPendingAppointmentsForStaff(staffProfile, (appointments) => {
        setPendingReviewAppointments(appointments);
        setLoadingPendingReview(false);
      });
    } else {
      setPendingReviewAppointments([]);
      setLoadingPendingReview(false);
    }

    // Subscribe to all CONFIRMED appointments
    unsubscribeConfirmed = appointmentService.subscribeToConfirmedAppointments((appointments) => {
      setConfirmedAppointments(appointments);
      setLoadingConfirmedAppointments(false);
    });

    return () => {
      unsubscribePro();
      unsubscribePending();
      unsubscribeConfirmed();
    };
  }, [staffProfile, canReviewAppointments]);

  // Set loadingCharts to false when all appointment data is loaded
  useEffect(() => {
    if (!loadingProAppointments && !loadingPendingReview && !loadingConfirmedAppointments) {
      setLoadingCharts(false);
    }
  }, [loadingProAppointments, loadingPendingReview, loadingConfirmedAppointments]);

  // Fetch user first name - cached to avoid repeated fetches
  useEffect(() => {
    const user = auth.currentUser;
    if (user && !currentUserFirstName) {
      const fetchUserDoc = async () => {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const fullName = userData.fullName || userData.name || "";
            const firstName = fullName.trim().split(" ")[0];
            setCurrentUserFirstName(firstName);
          } else if (user.displayName) {
            const firstName = user.displayName.trim().split(" ")[0];
            setCurrentUserFirstName(firstName);
          }
        } catch (err) {
          console.error("Error fetching user doc:", err);
        }
      };
      fetchUserDoc();
    }
  }, [auth.currentUser, currentUserFirstName]);

  const combinedAppointments = useMemo(() => {
    const deduped = new Map();
    [...pendingReviewAppointments, ...proAppointments, ...confirmedAppointments].forEach((appointment) => {
      deduped.set(appointment.id, appointment);
    });
    return Array.from(deduped.values());
  }, [pendingReviewAppointments, proAppointments, confirmedAppointments]);

  const analytics = useMemo(() => {
    return computeAnalytics(combinedAppointments, proAppointments);
  }, [combinedAppointments, proAppointments]);

  const latestProNotifications = useMemo(() => {
    return proNotifications.slice(0, 3);
  }, [proNotifications]);

  const totalNotifications = proNotifications.length;

  // New data fetching for Requests & Status Summary
  const requestsStatusData = useMemo(() => {
    const user = auth.currentUser;
    const allServices = ["Reproductive Health Consultation", "HIV Screening", "Family Planning"];
    
    // Filter services to only those assigned to the logged-in user
    const assignedServices = staffProfile?.assignServices || [];
    const services = allServices.filter(service => 
      assignedServices.some(assignedService => {
        const assignedLower = assignedService.toLowerCase();
        const serviceLower = service.toLowerCase();
        // Special handling for HIV Screening to also match STI Testing / Screening
        if (serviceLower === "hiv screening") {
          return assignedLower.includes("hiv") || assignedLower.includes("sti");
        }
        return assignedLower.includes(serviceLower) || serviceLower.includes(assignedLower);
      })
    );
    
    return services.map(service => {
      // Filter appointments by service name (case-insensitive and partial match)
      const serviceAppointments = combinedAppointments.filter(apt => {
        if (!apt.serviceName) return false;
        const aptServiceName = apt.serviceName.toLowerCase();
        const targetService = service.toLowerCase();
        // Special handling for HIV Screening to also match STI Testing / Screening
        if (targetService === "hiv screening") {
          return aptServiceName.includes("hiv") || aptServiceName.includes("sti");
        }
        return aptServiceName.includes(targetService) || targetService.includes(aptServiceName);
      });
      
      // Pending: all appointments with status PENDING
      const pending = serviceAppointments.filter(apt => apt.status === "PENDING").length;
      
      // Confirmed: all appointments with status CONFIRMED
      const confirmed = serviceAppointments.filter(apt => apt.status === "CONFIRMED").length;
      
      // Accepted: appointments with status ACCEPTED and proid matches logged in user
      const accepted = serviceAppointments.filter(apt => 
        apt.status === "ACCEPTED" && apt.proId === user?.uid
      ).length;
      
      return {
        name: service,
        Pending: pending,
        Confirmed: confirmed,
        Accepted: accepted
      };
    });
  }, [combinedAppointments, staffProfile]);

  // New data fetching for Weekly Appointment Trends
  const weeklyTrendsData = useMemo(() => {
    const user = auth.currentUser;
    const allServices = ["Reproductive Health Consultation", "HIV Screening", "Family Planning"];
    
    // Filter services to only those assigned to the logged-in user
    const assignedServices = staffProfile?.assignServices || [];
    const services = allServices.filter(service => 
      assignedServices.some(assignedService => {
        const assignedLower = assignedService.toLowerCase();
        const serviceLower = service.toLowerCase();
        // Special handling for HIV Screening to also match STI Testing / Screening
        if (serviceLower === "hiv screening") {
          return assignedLower.includes("hiv") || assignedLower.includes("sti");
        }
        return assignedLower.includes(serviceLower) || serviceLower.includes(assignedLower);
      })
    );
    
    // Get last 7 days
    const today = new Date();
    const last7Days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      last7Days.push(date);
    }
    
    return last7Days.map(date => {
      const dateStr = date.toISOString().split('T')[0];
      const formatDate = date.toLocaleDateString("en-US", { month: "short", day: "2-digit" });
      const fullDate = date.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
      
      // Filter appointments for this date and logged-in user's proId
      const dayAppointments = combinedAppointments.filter(apt => 
        apt.date === dateStr && apt.proId === user?.uid
      );
      
      // Count by status and service
      const completed = dayAppointments.filter(apt => apt.status === "COMPLETED");
      const cancelledByUser = dayAppointments.filter(apt => apt.status === "CANCELLED_BY_USER");
      const cancelledByPro = dayAppointments.filter(apt => apt.status === "CANCELLED_BY_PRO");
      
      // Count by service for each status
      const completedByService = services.map(service => {
        const serviceApts = completed.filter(apt => {
          if (!apt.serviceName) return false;
          const aptServiceName = apt.serviceName.toLowerCase();
          const targetService = service.toLowerCase();
          if (targetService === "hiv screening") {
            return aptServiceName.includes("hiv") || aptServiceName.includes("sti");
          }
          return aptServiceName.includes(targetService) || targetService.includes(aptServiceName);
        });
        return serviceApts.length;
      });
      
      const cancelledByUserByService = services.map(service => {
        const serviceApts = cancelledByUser.filter(apt => {
          if (!apt.serviceName) return false;
          const aptServiceName = apt.serviceName.toLowerCase();
          const targetService = service.toLowerCase();
          if (targetService === "hiv screening") {
            return aptServiceName.includes("hiv") || aptServiceName.includes("sti");
          }
          return aptServiceName.includes(targetService) || targetService.includes(aptServiceName);
        });
        return serviceApts.length;
      });
      
      const cancelledByProByService = services.map(service => {
        const serviceApts = cancelledByPro.filter(apt => {
          if (!apt.serviceName) return false;
          const aptServiceName = apt.serviceName.toLowerCase();
          const targetService = service.toLowerCase();
          if (targetService === "hiv screening") {
            return aptServiceName.includes("hiv") || aptServiceName.includes("sti");
          }
          return aptServiceName.includes(targetService) || targetService.includes(aptServiceName);
        });
        return serviceApts.length;
      });
      
      return {
        name: formatDate,
        fullDate: fullDate,
        completed: completed.length,
        cancelledByUser: cancelledByUser.length,
        cancelledByPro: cancelledByPro.length,
        completedByService: completedByService,
        cancelledByUserByService: cancelledByUserByService,
        cancelledByProByService: cancelledByProByService,
        services: services
      };
    });
  }, [combinedAppointments, staffProfile]);

  return (
    <div className="md-main">
      <div className="md-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px", padding: "16px 0" }}>
        <h2 className="pd-welcome" style={{ margin: 0, fontSize: "36px", fontWeight: 800, background: "linear-gradient(135deg, #2563eb 0%, #3b82f6 30%, #60a5fa 60%, #1e40af 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", letterSpacing: "-0.03em" }}>Welcome Back, {currentUserFirstName || "Medical Care Provider"}!</h2>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexShrink: 0 }}>
          <button
            type="button"
            className="md-action-btn"
            style={{ flex: "0 0 auto", width: "auto", minWidth: 0 }}
            onClick={() => setShowCalendar((current) => !current)}
          >
            <FiCalendar /> {showCalendar ? "Hide Schedule" : "View Schedule"}
          </button>
          <button
            type="button"
            className="md-action-btn"
            style={{ flex: "0 0 auto", width: "auto", minWidth: 0 }}
            onClick={() => setShowWalkinModal(true)}
          >
            <FiPlus /> Create Appointment
          </button>
        </div>
      </div>

      {showCalendar && (
        <section className="cc-analytics" style={{ marginBottom: "20px" }}>
          <header className="cc-section-header">
            <span>Appointment Calendar</span>
          </header>
          <div style={{ backgroundColor: "#fff", borderRadius: "12px", overflow: "hidden" }}>
            <iframe
              src="https://calendar.google.com/calendar/embed?src=auravit2026%40gmail.com&ctz=Asia%2FManila"
              style={{ border: 0, width: "100%", height: "600px", display: "block" }}
              frameBorder="0"
              scrolling="no"
              title="AuraVit Google Calendar"
            />
          </div>
        </section>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
        <section className="cc-analytics">
          <header className="cc-section-header">Requests & Status Summary</header>
          <div className="cc-chart-container">
            {loadingCharts ? (
              <div className="skeleton-chart">
                <div className="skeleton skeleton-header"></div>
                <div className="skeleton skeleton-subheader"></div>
                <div className="skeleton-bars-vertical">
                  {[1, 2, 3].map((index) => (
                    <div 
                      key={index} 
                      className="skeleton skeleton-bar-vertical" 
                      style={{ height: `${200 - (index * 50)}px`, width: '60px' }}
                    ></div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={requestsStatusData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                  >
                    <CartesianGrid stroke="#e0e7ff" strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fill: "#1e40af", fontSize: 10, fontWeight: 500 }}
                      height={40}
                    />
                    <YAxis 
                      tick={{ fill: "#1e40af" }}
                      allowDecimals={false}
                    />
                    <RechartsTooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div style={{ 
                              backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                              border: '1px solid rgba(59, 130, 246, 0.2)', 
                              borderRadius: '8px', 
                              padding: '12px',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                            }}>
                              <div style={{ 
                                fontWeight: 700, 
                                color: '#1e3a8a', 
                                marginBottom: '8px', 
                                fontSize: '13px' 
                              }}>{data.name}</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <div style={{ width: '10px', height: '10px', backgroundColor: '#f59e0b', borderRadius: '50%' }} />
                                  <span style={{ fontSize: '12px', color: '#1e40af' }}>{t.pendingCases}: <strong>{data.Pending}</strong></span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <div style={{ width: '10px', height: '10px', backgroundColor: '#3b82f6', borderRadius: '50%' }} />
                                  <span style={{ fontSize: '12px', color: '#1e40af' }}>{t.confirmed}: <strong>{data.Confirmed}</strong></span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <div style={{ width: '10px', height: '10px', backgroundColor: '#10b981', borderRadius: '50%' }} />
                                  <span style={{ fontSize: '12px', color: '#1e40af' }}>{t.accepted}: <strong>{data.Accepted}</strong></span>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <RechartsBar dataKey="Pending" name={t.pendingCases} fill="#f59e0b" />
                    <RechartsBar dataKey="Confirmed" name={t.confirmed} fill="#3b82f6" />
                    <RechartsBar dataKey="Accepted" name={t.accepted} fill="#10b981" />
                  </BarChart>
                </ResponsiveContainer>
                {/* Legend */}
                <div style={{ display: "flex", justifyContent: "center", gap: "20px", marginTop: "15px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <div style={{ width: "12px", height: "12px", backgroundColor: "#f59e0b" }}></div>
                    <span style={{ color: "#1e40af" }}>{t.pendingCases}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <div style={{ width: "12px", height: "12px", backgroundColor: "#3b82f6" }}></div>
                    <span style={{ color: "#1e40af" }}>{t.confirmed}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <div style={{ width: "12px", height: "12px", backgroundColor: "#10b981" }}></div>
                    <span style={{ color: "#1e40af" }}>{t.accepted}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>

        <section className="cc-analytics">
          <header className="cc-section-header">Daily Appointment Trends</header>
          <div className="cc-chart-container">
            {loadingCharts ? (
              <div className="skeleton-chart">
                <div className="skeleton skeleton-header"></div>
                <div className="skeleton skeleton-subheader"></div>
                <div className="skeleton skeleton-content"></div>
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={weeklyTrendsData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke="#e0e7ff" strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fill: "#1e40af" }} />
                    <YAxis tick={{ fill: "#1e40af" }} allowDecimals={false} />
                    <RechartsTooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          const services = data.services || ["Reproductive Health Consultation", "HIV Screening", "Family Planning"];
                          return (
                            <div style={{ 
                              backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                              border: '1px solid rgba(59, 130, 246, 0.2)', 
                              borderRadius: '8px', 
                              padding: '12px',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                              minWidth: '200px'
                            }}>
                              <div style={{ 
                                fontWeight: 700, 
                                color: '#1e3a8a', 
                                marginBottom: '10px', 
                                fontSize: '13px' 
                              }}>{data.fullDate}</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                    <div style={{ width: '10px', height: '10px', backgroundColor: '#10b981', borderRadius: '50%' }} />
                                    <span style={{ fontSize: '12px', color: '#1e40af', fontWeight: 600 }}>Completed: <strong>{data.completed}</strong></span>
                                  </div>
                                  <div style={{ marginLeft: '16px', fontSize: '11px', color: '#6b7280', textAlign: 'left' }}>
                                    {services.map((service, idx) => (
                                      <span key={idx}>{service}: {data.completedByService[idx]}{idx < services.length - 1 ? ', ' : ''}</span>
                                    ))}
                                  </div>
                                </div>
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                    <div style={{ width: '10px', height: '10px', backgroundColor: '#ef4444', borderRadius: '50%' }} />
                                    <span style={{ fontSize: '12px', color: '#1e40af', fontWeight: 600 }}>Cancelled by User: <strong>{data.cancelledByUser}</strong></span>
                                  </div>
                                  <div style={{ marginLeft: '16px', fontSize: '11px', color: '#6b7280', textAlign: 'left' }}>
                                    {services.map((service, idx) => (
                                      <span key={idx}>{service}: {data.cancelledByUserByService[idx]}{idx < services.length - 1 ? ', ' : ''}</span>
                                    ))}
                                  </div>
                                </div>
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                    <div style={{ width: '10px', height: '10px', backgroundColor: '#f59e0b', borderRadius: '50%' }} />
                                    <span style={{ fontSize: '12px', color: '#1e40af', fontWeight: 600 }}>Cancelled by Pro: <strong>{data.cancelledByPro}</strong></span>
                                  </div>
                                  <div style={{ marginLeft: '16px', fontSize: '11px', color: '#6b7280', textAlign: 'left' }}>
                                    {services.map((service, idx) => (
                                      <span key={idx}>{service}: {data.cancelledByProByService[idx]}{idx < services.length - 1 ? ', ' : ''}</span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <RechartsLine type="monotone" dataKey="completed" name="Completed" stroke="#10b981" strokeWidth={3} dot={{ r: 4, fill: "#10b981" }} />
                    <RechartsLine type="monotone" dataKey="cancelledByUser" name="Cancelled by User" stroke="#ef4444" strokeWidth={3} dot={{ r: 4, fill: "#ef4444" }} />
                    <RechartsLine type="monotone" dataKey="cancelledByPro" name="Cancelled by Pro" stroke="#f59e0b" strokeWidth={3} dot={{ r: 4, fill: "#f59e0b" }} />
                  </LineChart>
                </ResponsiveContainer>
                {/* Legend */}
                <div style={{ display: "flex", justifyContent: "center", gap: "20px", marginTop: "15px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <div style={{ width: "12px", height: "12px", backgroundColor: "#10b981" }}></div>
                    <span style={{ color: "#1e40af" }}>Completed</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <div style={{ width: "12px", height: "12px", backgroundColor: "#ef4444" }}></div>
                    <span style={{ color: "#1e40af" }}>Cancelled by User</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <div style={{ width: "12px", height: "12px", backgroundColor: "#f59e0b" }}></div>
                    <span style={{ color: "#1e40af" }}>Cancelled by Pro</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
        <section className="cc-analytics">
          <header className="cc-section-header">{t.reviewAppointments}</header>
          <div className="md-appointment-list" style={{ maxHeight: "400px", overflowY: "auto" }}>
            {loadingPendingReview ? (
              [1, 2, 3, 4, 5].map((index) => (
                <div key={index} className="md-appointment-item">
                  <div className="skeleton" style={{ width: "40px", height: "40px", borderRadius: "50%" }}></div>
                  <div className="md-appointment-details">
                    <div className="skeleton skeleton-header"></div>
                    <div className="skeleton skeleton-subheader"></div>
                    <div className="skeleton" style={{ width: "100px", height: "12px" }}></div>
                  </div>
                  <div className="md-appointment-actions">
                    <div className="skeleton" style={{ width: "20px", height: "20px", borderRadius: "4px" }}></div>
                    <div className="skeleton" style={{ width: "20px", height: "20px", borderRadius: "4px" }}></div>
                  </div>
                </div>
              ))
            ) : (
              <>
                {pendingReviewAppointments.length === 0 && !loadingPendingReview ? (
                  <div style={{ textAlign: "center", padding: "30px", color: "#9ca3af" }}>
                    {t.noPendingAppointments}
                  </div>
                ) : (
                  pendingReviewAppointments.slice(0, 5).map((appointment) => (
                    <div key={appointment.id} className="md-appointment-item" style={{ cursor: onGoToReview ? 'pointer' : 'default' }} onClick={() => onGoToReview?.()}>
                      <div className="md-appointment-avatar">
                        {(appointment.patientName || "?").charAt(0).toUpperCase()}
                      </div>
                      <div className="md-appointment-details">
                        <div className="md-appointment-name">{appointment.patientName}</div>
                        <div className="md-appointment-topic">{appointment.serviceName}</div>
                        <div className="md-appointment-time">
                          {appointment.time} • {new Date(`${appointment.date}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        </div>
                      </div>
                      <div className="md-appointment-actions">
                        <FiEye style={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); onGoToReview?.(); }} />
                        <FiEdit style={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); onGoToReview?.(); }} />
                      </div>
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        </section>

        <section className="cc-analytics">
          <header className="cc-section-header">Today Appointments</header>
          <div className="md-appointment-list" style={{ maxHeight: "400px", overflowY: "auto" }}>
            {loadingProAppointments ? (
              [1, 2, 3, 4, 5].map((index) => (
                <div key={index} className="md-appointment-item">
                  <div className="skeleton" style={{ width: "40px", height: "40px", borderRadius: "50%" }}></div>
                  <div className="md-appointment-details">
                    <div className="skeleton skeleton-header"></div>
                    <div className="skeleton skeleton-subheader"></div>
                    <div className="skeleton" style={{ width: "80px", height: "12px" }}></div>
                  </div>
                  <div className="skeleton" style={{ width: "20px", height: "20px", borderRadius: "4px" }}></div>
                </div>
              ))
            ) : (
              <>
                {analytics.todayAppointments.length === 0 && !loadingCharts ? (
                  <div style={{ textAlign: "center", padding: "30px", color: "#9ca3af" }}>
                    No appointments today
                  </div>
                ) : (
                  analytics.todayAppointments.map((appointment) => (
                    <div key={appointment.id} className="md-appointment-item">
                      <div className="md-appointment-avatar">
                        {(appointment.patientName || "?").charAt(0).toUpperCase()}
                      </div>
                      <div className="md-appointment-details">
                        <div className="md-appointment-name">{appointment.patientName}</div>
                        <div className="md-appointment-topic">{appointment.serviceName}</div>
                        <div className="md-appointment-time">{appointment.time}</div>
                      </div>
                      {appointment.status === "PENDING" && (
                        <div style={{ display: "flex", gap: "8px" }}>
                          <FaCheck style={{ color: "#10b981", cursor: "pointer", fontSize: "18px" }} />
                          <FaTimes style={{ color: "#ef4444", cursor: "pointer", fontSize: "18px" }} />
                        </div>
                      )}
                      {appointment.status === "CONFIRMED" && (
                        <FiCheckCircle style={{ color: "#3b82f6", fontSize: "20px" }} />
                      )}
                      {appointment.status === "ACCEPTED" && (
                        <FiCheckCircle style={{ color: "#8b5cf6", fontSize: "20px" }} />
                      )}
                      {appointment.status === "COMPLETED" && (
                        <FiCheckCircle style={{ color: "#10b981", fontSize: "20px" }} />
                      )}
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        </section>
      </div>

      <section className="cc-analytics">
        <header className="cc-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="md-notification-header">
            <span>Recent Access Notifications</span>
            <span className="md-notification-badge">{totalNotifications}</span>
          </div>
          {onGoToNotifications && (
            <button
              type="button"
              onClick={onGoToNotifications}
              style={{
                background: 'transparent',
                border: '1px solid #d1d5db',
                borderRadius: '999px',
                padding: '6px 14px',
                color: '#1f2937',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              View all
            </button>
          )}
        </header>
        <div className="md-notification-list">
          {loadingProAppointments ? (
            [1, 2, 3].map((index) => (
              <div key={index} className="md-notification-item">
                <div className="skeleton" style={{ width: "40px", height: "40px", borderRadius: "50%" }}></div>
                <div className="md-notification-content">
                  <div className="skeleton skeleton-header"></div>
                  <div className="skeleton skeleton-subheader"></div>
                  <div className="skeleton" style={{ width: "80px", height: "12px" }}></div>
                </div>
              </div>
            ))
          ) : (
            <>
              {latestProNotifications.length === 0 && !loadingProAppointments ? (
                <div className="md-empty-state" style={{ textAlign: 'center', padding: '30px', color: '#9ca3af' }}>
                  No recent access notifications.
                </div>
              ) : (
                latestProNotifications.map((notif) => (
                  <div key={notif.id} className="md-notification-item" style={{ cursor: 'default' }}>
                    <div className="md-notification-icon">
                      {notif.icon || <FaBell />}
                    </div>
                    <div className="md-notification-content">
                      <div className="md-notification-title">{notif.title}</div>
                      <div className="md-notification-body" style={{ fontSize: '13px', color: '#6b7280', marginTop: '6px' }}>{notif.desc}</div>
                      <div className="md-notification-time">{notif.time}</div>
                    </div>
                  </div>
                ))
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default MedicalDashboard;
